import React, { useState, useEffect } from 'react';
import * as Sentry from "@sentry/react";
import { Bookmark, Compass } from 'lucide-react';
import { getMySavedPosts } from '../../services/api';
import PostCard from '../../components/community/PostCard';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CommunityError } from "../../errors/sentryErrors";

const MySaved = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    fetchSaved();
  }, []);

  const fetchSaved = async () => {
    try {
      const data = await getMySavedPosts();
      setPosts(data.posts || []);
      setNextCursor(data.nextCursor);
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(error instanceof Error ? error.message : String(error)));
      });
      toast.error('Failed to load saved trips');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await getMySavedPosts(nextCursor);
      setPosts(prev => [...prev, ...data.posts]);
      setNextCursor(data.nextCursor);
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(error instanceof Error ? error.message : String(error)));
      });
      toast.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-24">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center">
              <Bookmark size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Saved Trips</h1>
              <p className="text-gray-500">Your personal collection of travel inspiration</p>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="aspect-[4/4.6] bg-gray-200 rounded-[24px] animate-pulse" />
              ))}
            </div>
          ) : posts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {posts.map(post => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
              
              {nextCursor && (
                <div className="mt-10 flex justify-center">
                  <button 
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-8 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-colors shadow-sm"
                  >
                    {loadingMore ? 'Loading...' : 'Load More Saved Trips'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-gray-100">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Bookmark size={32} className="text-gray-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No saved trips yet</h2>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">
                Explore the community and click the bookmark icon to save trips you'd like to reference later.
              </p>
              <Link to="/community" className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors">
                <Compass size={20} />
                Explore Community
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MySaved;
